import { Dropdown } from 'primereact/dropdown';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ascending from '../../../../assets/ascending.png';
import descending from '../../../../assets/descending.png';
import leftArrow from '../../../../assets/leftArrow.png';
import {
    useCreateMetricMutation,
    useGetAllParentClientsQuery,
    useGetMetricByIdQuery,
    useGetMetricCategoriesQuery,
    useGetMetricFrequenciesQuery,
    useGetMetricTypesQuery,
    useLazyCheckMetricNameQuery,
    useParseFormulaMutation,
    useUpdateMetricMutation,
} from '../../metricsAdminSlice';
import { ConfirmationDialog } from '../confirmationDialog/ConfirmationDialog';
import './MetricsCreation.scss';

interface MetricsCreationProps {
    metricId?: number | null;
    onCancel?: () => void;
    onSave?: () => void;
}

interface FormulaElement {
    metric_element_id: number;
    metric_element_name: string;
    metric_element_description: string;
}

export const MetricsCreation: React.FC<MetricsCreationProps> = ({ metricId, onCancel, onSave }) => {
    const [metricName, setMetricName] = useState('');
    const [metricNameError, setMetricNameError] = useState<string>('');
    const [isCheckingName, setIsCheckingName] = useState(false);
    const [label, setLabel] = useState('');
    const [shortDescription, setShortDescription] = useState('');
    const [longDescription, setLongDescription] = useState('');
    const [dataSource, setDataSource] = useState('');
    const [parentClient, setParentClient] = useState<number | null>(null);
    const [metricCategory, setMetricCategory] = useState<number | null>(null);
    const [dataType, setDataType] = useState<number | null>(null);
    const [isGlobalMetric, setIsGlobalMetric] = useState(false);
    const [frequency, setFrequency] = useState<number | null>(null);
    const [status, setStatus] = useState('Active');
    const [metricDirection, setMetricDirection] = useState('1');
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [formula, setFormula] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [formulaElements, setFormulaElements] = useState<FormulaElement[]>([]);
    const [elementDescriptions, setElementDescriptions] = useState<Record<number, string>>({});
    const [isParsingFormula, setIsParsingFormula] = useState(false);

    const isCancellingRef = useRef(false);

    const parseDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isEditMode = metricId !== null && metricId !== undefined;

    // Pre-compute button labels to avoid nested ternaries in JSX
    const savingLabel = isEditMode ? 'Updating...' : 'Saving...';
    const saveLabel = isEditMode ? 'Update' : 'Save';

    const [createMetric] = useCreateMetricMutation();
    const [updateMetric] = useUpdateMetricMutation();
    const [parseFormula] = useParseFormulaMutation();
    const [checkMetricName] = useLazyCheckMetricNameQuery();

    const { data: parentClientsResponse } = useGetAllParentClientsQuery();
    const parentClients = parentClientsResponse?.data || [];

    const { data: metricCategoriesResponse } = useGetMetricCategoriesQuery();
    const metricCategories = metricCategoriesResponse?.data || [];

    const { data: metricFrequenciesResponse } = useGetMetricFrequenciesQuery();
    const metricFrequencies = metricFrequenciesResponse?.data || [];

    const { data: metricTypesResponse } = useGetMetricTypesQuery();
    const metricTypes = metricTypesResponse?.data || [];

    const { data: metricDataResponse, isLoading: isLoadingMetric } = useGetMetricByIdQuery(metricId || 0, {
        skip: !isEditMode,
    });

    // Populate form when editing
    useEffect(() => {
        if (isEditMode && metricDataResponse?.data) {
            const metric = metricDataResponse.data;
            setMetricName(metric.metric_name || '');
            setLabel(metric.label || '');
            setShortDescription(metric.metric_description || '');
            setLongDescription(metric.metric_long_desc || '');
            setDataSource(metric.data_sources || '');
            setParentClient(metric.parent_client_id || null);
            setMetricCategory(metric.metric_category_id || null);
            setDataType(metric.metric_type_id || null);
            setIsGlobalMetric(metric.parent_client_id === null);
            setFrequency(metric.metric_frequency_id || null);
            setStatus(metric.status ? 'Active' : 'Inactive');
            setMetricDirection(metric.metric_direction_id?.toString() || '1');
            setFormula(metric.formula || '');

            if (metric.formula) {
                handleParseFormula(metric.formula).then(() => {
                    if (metric.metric_elements) {
                        const elements = typeof metric.metric_elements === 'string' 
                            ? JSON.parse(metric.metric_elements) : metric.metric_elements;
                        if (Array.isArray(elements)) {
                            const saved: Record<number, string> = {};
                            elements.forEach((el: any) => {
                                if (el.metric_element_id && el.element_description !== undefined) {
                                    saved[el.metric_element_id] = el.element_description;
                                }
                            });
                            setElementDescriptions(prev => ({ ...prev, ...saved }));
                        }
                    }
                });
            }
        }
    }, [isEditMode, metricDataResponse]);

    // Duplicate-name check
    const handleMetricNameBlur = async (forceGlobal = false) => {
        if (isCancellingRef.current) {
            isCancellingRef.current = false;
            return;
        }

        const trimmed = metricName.trim();

        if (!trimmed || (!isGlobalMetric && !forceGlobal)) {
            setMetricNameError('');
            return;
        }

        if (isEditMode && metricDataResponse?.data?.metric_name === trimmed) {
            setMetricNameError('');
            return;
        }

        setIsCheckingName(true);
        try {
            const response = await checkMetricName({
                name: trimmed,
                excludeMetricId: isEditMode ? metricId : null,
            }).unwrap();

            if (response?.data?.exists) {
                setMetricNameError(`"${trimmed}" is already in use. Please choose a different metric name.`);
                toast.error(`"${trimmed}" is already in use. Please choose a different metric name.`);
            } else {
                setMetricNameError('');
            }
        } catch {
            setMetricNameError('');
        } finally {
            setIsCheckingName(false);
        }
    };

    const handleMetricNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMetricName(e.target.value);
        if (metricNameError) setMetricNameError('');
    };

    // Parse formula to get elements
    const handleParseFormula = async (formulaText: string) => {
        if (!formulaText.trim()) {
            setFormulaElements([]);
            setElementDescriptions({});
            return;
        }

        setIsParsingFormula(true);
        try {
            console.log('Parsing formula:', formulaText);
            const response = await parseFormula(formulaText).unwrap();

            console.log('Parse formula response:', response);

            if (response?.data && Array.isArray(response.data)) {
                setFormulaElements(response.data);

                setElementDescriptions(prev => {
                    const newDescriptions = { ...prev };
                    response.data.forEach((element: FormulaElement) => {
                        if (!(element.metric_element_id in newDescriptions)) {
                            newDescriptions[element.metric_element_id] = element.metric_element_description || '';
                        }
                    });
                    const currentElementIds = new Set(response.data.map((e: FormulaElement) => e.metric_element_id));
                    Object.keys(newDescriptions).forEach(key => {
                        const elementId = parseInt(key, 10);
                        if (!currentElementIds.has(elementId)) {
                            delete newDescriptions[elementId];
                        }
                    });
                    return newDescriptions;
                });
            } else {
                console.warn('No elements found in formula');
                setFormulaElements([]);
                setElementDescriptions({});
            }
        } catch (error: any) {
            console.error('Error parsing formula:', error);

            if (error?.status === 404) {
                toast.error('Formula parsing endpoint not found. Please contact support.');
            } else if (error?.status === 500) {
                toast.error('Server error while parsing formula');
            } else if (error?.status === 400) {
                console.warn('Invalid formula format');
            } else if (error?.data?.message) {
                toast.error(error.data.message);
            }

            setFormulaElements([]);
            setElementDescriptions({});
        } finally {
            setIsParsingFormula(false);
        }
    };

    // Handle formula change
    const handleFormulaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newFormula = e.target.value;
        setFormula(newFormula);

        if (parseDebounceRef.current) clearTimeout(parseDebounceRef.current);

        parseDebounceRef.current = setTimeout(() => {
            handleParseFormula(newFormula);
        }, 800);
    };

    const validateForm = (): boolean => {
        if (!metricName.trim()) {
            toast.error('Metric Name is required');
            return false;
        }
        if (isGlobalMetric && metricNameError) {
            toast.error(metricNameError);
            return false;
        }
        if (!label.trim()) {
            toast.error('Label is required');
            return false;
        }
        if (!metricCategory) {
            toast.error('Metric Category is required');
            return false;
        }
        if (!dataType) {
            toast.error('Data Type is required');
            return false;
        }
        if (!frequency) {
            toast.error('Frequency is required');
            return false;
        }
        if (!metricDirection) {
            toast.error('Metric Direction is required');
            return false;
        }
        if (!isGlobalMetric && !parentClient) {
            toast.error('Parent Client is required for non-global metrics');
            return false;
        }
        return true;
    };

    const handleSave = () => {
        if (validateForm()) {
            setShowSaveDialog(true);
        }
    };

    const handleCancel = () => {
        isCancellingRef.current = true;
        setShowCancelDialog(true);
    };

    const confirmSave = async () => {
        setShowSaveDialog(false);
        setIsSaving(true);

        try {
            const metricData = {
                metricName: metricName.trim(),
                label: label.trim(),
                formula: formula.trim() || null,
                metricDescription: shortDescription.trim() || null,
                metricLongDesc: longDescription.trim() || null,
                metricCategoryId: metricCategory,
                metricTypeId: dataType,
                parentClientId: isGlobalMetric ? null : parentClient,
                metricDirectionId: parseInt(metricDirection, 10),
                metricFrequencyId: frequency,
                lastFeedDay: null,
                dataSources: dataSource.trim() || null,
                status: status === 'Active',
                elements: formulaElements.map((element) => ({
                    metricElementId: element.metric_element_id,
                    elementDescription:
                        elementDescriptions[element.metric_element_id] !== undefined
                            ? elementDescriptions[element.metric_element_id]
                            : element.metric_element_description,
                })),
            };

            let response;
            if (isEditMode) {
                response = await updateMetric({
                    metricId,
                    ...metricData,
                }).unwrap();
            } else {
                response = await createMetric(metricData).unwrap();
            }

            if (response.data && response.data.success) {
                toast.success(
                    response.data.message || `Metric ${isEditMode ? 'updated' : 'created'} successfully!`
                );

                // Reset form only if creating new
                if (!isEditMode) {
                    setMetricName('');
                    setMetricNameError('');
                    setLabel('');
                    setShortDescription('');
                    setLongDescription('');
                    setDataSource('');
                    setParentClient(null);
                    setMetricCategory(null);
                    setDataType(null);
                    setIsGlobalMetric(false);
                    setFrequency(null);
                    setStatus('Active');
                    setMetricDirection('1');
                    setFormula('');
                    setFormulaElements([]);
                    setElementDescriptions({});
                }

                if (onSave) {
                    onSave();
                }
            } else {
                toast.error(response.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} metric`);
            }
        } catch (error: any) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} metric:`, error);

            if (error?.status === 409) {
                const msg = error.data?.message || 'A metric with this name already exists.';
                setMetricNameError(msg);
                toast.error(msg);
            } else if (error?.data?.message) {
                toast.error(error.data.message);
            } else if (error?.message) {
                toast.error(error.message);
            } else {
                toast.error(
                    `An error occurred while ${
                        isEditMode ? 'updating' : 'creating'
                    } the metric. Please try again.`
                );
            }
        } finally {
            setIsSaving(false);
        }
    };

    const confirmCancel = () => {
        console.log('Cancel metrics creation/edit');
        setShowCancelDialog(false);

        if (onCancel) {
            onCancel();
        }
    };

    const handleBackClick = () => {
        if (onCancel) {
            onCancel();
        }
    };

    const removeFocusHighlight = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const el = e.currentTarget;
        el.style.boxShadow = 'none';
        el.style.borderColor = '#d3d3d3';
        el.style.outline = 'none';
        el.style.backgroundColor = '#FFFFFF';
    };

    if (isEditMode && isLoadingMetric) {
        return <div className="text-center p-5">Loading metric data...</div>;
    }

    return (
        <div className="metrics-creation-page">
            {/* Page Title - Outside the form card */}
            <h4 className="metrics-admin-title mb-4">Metrics Admin</h4>

            {/* Main Form Card */}
            <div className="card shadow-sm bg-white rounded border-0 metrics-form-card">
                <div className="card-body p-4">
                    {/* Metric Creation/Edit Header with Back Button */}
                    <div
                        className="section-header mb-4"
                        style={{ cursor: 'pointer' }}
                        onClick={handleBackClick}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleBackClick();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Go back to metrics list"
                    >
                        <img src={leftArrow} alt="Back" className="me-2" />
                        <span>{isEditMode ? 'Edit Metric' : 'Metric Creation'}</span>
                    </div>

                    {/* Metric Information Section */}
                    <div className="metric-information-section">
                        <h6 className="section-subtitle mb-4">Metric Information</h6>

                        {/* Two Column Layout */}
                        <div className="row">
                            {/* Left Column - All Form Fields */}
                            <div className="col-md-6">
                                {/* Metric Name */}
                                <div className="mb-3">
                                    <label htmlFor="metricName" className="form-label">
                                        Metric Name
                                    </label>
                                    <input
                                        id="metricName"
                                        type="text"
                                        className="form-control"
                                        placeholder="Input"
                                        value={metricName}
                                        onChange={handleMetricNameChange}
                                        onBlur={() => handleMetricNameBlur()}
                                        onFocus={removeFocusHighlight}
                                    />
                                </div>

                                {/* Label */}
                                <div className="mb-3">
                                    <label htmlFor="label" className="form-label">
                                        Label
                                    </label>
                                    <input
                                        id="label"
                                        type="text"
                                        className="form-control"
                                        placeholder="Input"
                                        value={label}
                                        onChange={(e) => setLabel(e.target.value)}
                                        onFocus={removeFocusHighlight}
                                    />
                                </div>

                                {/* Short Description */}
                                <div className="mb-3">
                                    <label htmlFor="shortDescription" className="form-label">
                                        Short Description
                                    </label>
                                    <input
                                        id="shortDescription"
                                        type="text"
                                        className="form-control"
                                        placeholder="Input"
                                        value={shortDescription}
                                        onChange={(e) => setShortDescription(e.target.value)}
                                        onFocus={removeFocusHighlight}
                                    />
                                </div>

                                {/* Long Description */}
                                <div className="mb-3">
                                    <label htmlFor="longDescription" className="form-label">
                                        Long Description
                                    </label>
                                    <textarea
                                        id="longDescription"
                                        className="form-control"
                                        rows={4}
                                        placeholder="Input"
                                        value={longDescription}
                                        onChange={(e) => setLongDescription(e.target.value)}
                                    />
                                </div>

                                {/* Data Source */}
                                <div className="mb-3">
                                    <label htmlFor="dataSource" className="form-label">
                                        Data Source
                                    </label>
                                    <input
                                        id="dataSource"
                                        type="text"
                                        className="form-control"
                                        placeholder="Input"
                                        value={dataSource}
                                        onChange={(e) => setDataSource(e.target.value)}
                                        onFocus={removeFocusHighlight}
                                    />
                                </div>
                                <hr className="form-divider" />

                                {/* Parent Client, Metric Category, Data Type fields */}
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label htmlFor="parentClient" className="form-label">
                                            Parent Client
                                        </label>
                                        <Dropdown
                                            id="parentClient"
                                            value={parentClient}
                                            options={parentClients}
                                            onChange={(e) => setParentClient(e.value)}
                                            optionLabel="parent_client_name"
                                            optionValue="parent_client_id"
                                            placeholder="Select"
                                            disabled={isGlobalMetric}
                                            style={{ width: '100%', borderRadius: '24px' }}
                                            className="metrics-dropdown"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="metricCategory" className="form-label">
                                            Metric Category
                                        </label>
                                        <Dropdown
                                            id="metricCategory"
                                            value={metricCategory}
                                            options={metricCategories}
                                            onChange={(e) => setMetricCategory(e.value)}
                                            optionLabel="metric_category_name"
                                            optionValue="metric_category_id"
                                            placeholder="Select"
                                            style={{ width: '100%', borderRadius: '24px' }}
                                            className="metrics-dropdown"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="dataType" className="form-label">
                                            Data Type
                                        </label>
                                        <Dropdown
                                            id="dataType"
                                            value={dataType}
                                            options={metricTypes}
                                            onChange={(e) => setDataType(e.value)}
                                            optionLabel="metric_type_name"
                                            optionValue="metric_type_id"
                                            placeholder="Select"
                                            style={{ width: '100%', borderRadius: '24px' }}
                                            className="metrics-dropdown"
                                        />
                                    </div>
                                </div>

                                {/* Global Metric Checkbox */}
                                <div className="mb-3">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="globalMetric"
                                            checked={isGlobalMetric}
                                            onChange={(e) => {
                                                setIsGlobalMetric(e.target.checked);
                                                if (e.target.checked) {
                                                    setParentClient(null);
                                                } else {
                                                    setMetricNameError('');
                                                }
                                            }}
                                        />
                                        <label className="form-check-label" htmlFor="globalMetric">
                                            Global Metric
                                        </label>
                                    </div>
                                </div>

                                {/* Frequency and Status */}
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label htmlFor="frequency" className="form-label">
                                            Frequency
                                        </label>
                                        <Dropdown
                                            id="frequency"
                                            value={frequency}
                                            options={metricFrequencies}
                                            onChange={(e) => setFrequency(e.value)}
                                            optionLabel="metric_frequency_name"
                                            optionValue="metric_frequency_id"
                                            placeholder="Select"
                                            style={{ width: '100%', borderRadius: '24px' }}
                                            className="metrics-dropdown"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="status" className="form-label">
                                            Status
                                        </label>
                                        <Dropdown
                                            id="status"
                                            value={status}
                                            options={[
                                                { label: 'Active', value: 'Active' },
                                                { label: 'Inactive', value: 'Inactive' },
                                            ]}
                                            onChange={(e) => setStatus(e.value)}
                                            placeholder="Select"
                                            style={{ width: '100%', borderRadius: '24px' }}
                                            className="metrics-dropdown"
                                        />
                                    </div>
                                </div>

                                {/* Metric Direction */}
                                <div className="mb-1 mt-4">
                                    <div className="form-label d-block mb-2">Metric Direction</div>

                                    <div className="row gx-3">
                                        {/* Ascending */}
                                        <div className="col-md-6 mb-3">
                                            <div className="metric-direction-wrapper">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="metricDirection"
                                                        id="ascending"
                                                        value="1"
                                                        checked={metricDirection === '1'}
                                                        onChange={(e) => setMetricDirection(e.target.value)}
                                                    />
                                                    <label className="form-check-label" htmlFor="ascending">
                                                        <div className="direction-header">
                                                            <img src={ascending} alt="up" className="me-2" />
                                                            <span className="direction-title">
                                                                Ascending
                                                            </span>
                                                        </div>
                                                    </label>
                                                </div>
                                                <p className="direction-description">
                                                    A metric where a higher value indicates better
                                                    performance or a more favorable outcome.
                                                </p>
                                            </div>
                                        </div>

                                        {/* Descending */}
                                        <div className="col-md-6 mb-3">
                                            <div className="metric-direction-wrapper">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="metricDirection"
                                                        id="descending"
                                                        value="2"
                                                        checked={metricDirection === '2'}
                                                        onChange={(e) => setMetricDirection(e.target.value)}
                                                    />
                                                    <label className="form-check-label" htmlFor="descending">
                                                        <div className="direction-header">
                                                            <img
                                                                src={descending}
                                                                alt="down"
                                                                className="me-2"
                                                            />
                                                            <span className="direction-title">
                                                                Descending
                                                            </span>
                                                        </div>
                                                    </label>
                                                </div>
                                                <p className="direction-description">
                                                    A metric where a lower value indicates better
                                                    performance or a more favorable outcome.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Formula and Elements */}
                            <div className="col-md-6">
                                {/* Formula */}
                                <div className="mb-3">
                                    <label htmlFor="formula" className="form-label">
                                        Formula
                                    </label>
                                    <textarea
                                        id="formula"
                                        className="form-control formula-input"
                                        rows={5}
                                        placeholder="Enter formula"
                                        value={formula}
                                        onChange={handleFormulaChange}
                                    />
                                    {isParsingFormula && (
                                        <small className="text-muted d-block mt-1">
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            Parsing formula...
                                        </small>
                                    )}
                                </div>

                                <hr className="formula-elements-divider" />

                                {/* Elements */}
                                <div className="mb-3">
                                    <div className="form-label d-block mb-2">Elements</div>
                                    <div className="elements-container">
                                        <div className="elements-header">
                                            <div className="row g-0">
                                                <div className="col-6">
                                                    <span className="elements-header-text">
                                                        Element Label
                                                    </span>
                                                </div>
                                                <div className="col-6">
                                                    <span className="elements-header-text">
                                                        Element Short Description
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="elements-content">
                                            {formulaElements.length === 0 ? (
                                                <div className="elements-empty-state text-muted text-center p-4">
                                                    {!formula.trim() ? (
                                                        <>
                                                            <i className="pi pi-info-circle me-2" />
                                                            Enter a formula to automatically detect elements
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="pi pi-exclamation-triangle me-2" />
                                                            No elements found in formula
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <>
                                                    {formulaElements.map((element) => (
                                                        <div
                                                            className="elements-row"
                                                            key={element.metric_element_id}
                                                        >
                                                            <div className="row g-0 align-items-center">
                                                                <div className="col-6">
                                                                    <span className="element-label">
                                                                        {element.metric_element_name}
                                                                    </span>
                                                                </div>
                                                                <div className="col-6">
                                                                    <input
                                                                        type="text"
                                                                        className="form-control element-input"
                                                                        placeholder="Input"
                                                                        value={
                                                                            elementDescriptions[
                                                                                element.metric_element_id
                                                                            ] || ''
                                                                        }
                                                                        onChange={(e) => {
                                                                            setElementDescriptions({
                                                                                ...elementDescriptions,
                                                                                [element.metric_element_id]:
                                                                                    e.target.value,
                                                                            });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="row mt-5">
                            <div className="col-md-12">
                                <div className="action-buttons">
                                    <button
                                        type="button"
                                        className="btn btn-cancel"
                                        onMouseDown={() => {
                                            isCancellingRef.current = true;
                                        }}
                                        onClick={handleCancel}
                                    >
                                        <i className="pi pi-times-circle me-2" />
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-save"
                                        onClick={handleSave}
                                        disabled={isSaving || isCheckingName || !!metricNameError}
                                    >
                                        {isSaving ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                {savingLabel}
                                            </>
                                        ) : (
                                            <>
                                                <i className="pi pi-check-circle me-2" />
                                                {saveLabel}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmationDialog
                visible={showCancelDialog}
                onHide={() => setShowCancelDialog(false)}
                onConfirm={confirmCancel}
                type="cancel"
            />

            <ConfirmationDialog
                visible={showSaveDialog}
                onHide={() => setShowSaveDialog(false)}
                onConfirm={confirmSave}
                type="save"
            />
        </div>
    );
};
